import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProcessContext from '../../../contexts/process/ProcessContext';
import StatusBarButton from '../../layout/StatusBarButton';
import NumberPad from '../../ui-elements/NumberPad';
import Loading from '../../ui-elements/Loading';
import UIContext from '../../../contexts/ui/UIContext';
import BackButton from '../../layout/BackButton';
import { HELP_DESK_LINK } from '../../../constants/helpDesk';


const PanelSelector = ({ forwardTo, panel }) => {
    const { activeProcessId, continueProcess, notifyError } = useContext(ProcessContext);
    const { loading } = useContext(UIContext);
    const [display, setDisplay] = useState({
        prefix: 'p',
        value: ''
    });
    const autoStartedRef = useRef(false)
    const validPanelNamePattern = /^p(?:[1-9]|[1-7]\d|8[0-8])$/;
    const navigate = useNavigate();

    useEffect(() => {
      if (panel && validPanelNamePattern.test(panel)) {
        setDisplay({ prefix: panel[0], value: panel.slice(1) });
      }
    }, [panel]);

    useEffect(() => {
      if (panel && display.value && !autoStartedRef.current) {
        autoStartedRef.current = true;

        const panelName = `${display.prefix}${display.value}`;
        if (!validPanelNamePattern.test(panelName)) {
          notifyError(`Invalid panel “${panelName}”. Please select between p1 and p${import.meta.env.VITE_MAX_PANEL_NUMBER}.`);
          return;
        }

        continueProcess(activeProcessId, panelName)
          .then(() => {
            if (forwardTo) {
              navigate(forwardTo);
            }
          })
          .catch((error) => {
            notifyError(error.message);
            console.error('Auto-continue PanelSelector failed:', error);
          });
      }
    }, [display]);

    const proceed = useCallback(async () => {
      if (parseInt(display.value) > parseInt(import.meta.env.VITE_MAX_PANEL_NUMBER) || parseInt(display.value) < 1) {
        notifyError(`You tried to enter an invalid Panel. Please select a panel between 1 and ${import.meta.env.VITE_MAX_PANEL_NUMBER}`)
        return;
      }

      const panelName = `${display.prefix}${display.value}`;

      try {
          await continueProcess(activeProcessId, panelName);
          if (forwardTo) {
            navigate(forwardTo);
          }
        }
        catch {
          notifyError(<>We have encountered a problem while continuing the Process. Please contact the support team {HELP_DESK_LINK}.</>)
        }
      }, [activeProcessId, continueProcess, display, forwardTo, navigate]);

      const setValue = (value) => {
        setDisplay(prev => ({ ...prev, value }))
    };

    return (loading ?
        <Loading fullScreen /> :
        <Fragment>
            <NumberPad prefix={display.prefix} value={display.value} setValue={setValue} />
            {display.value && <StatusBarButton label='Continue' icon='fas fa-chevron-right' reversed={true} clickHandler={proceed} statusSlot={5} />}
            <BackButton />
        </Fragment>
    )
}

export default PanelSelector
